import { useCallback, useEffect, useState } from 'react';
import Modal, { ModalButton } from '../dm/Modal';
import { useServer, useServers } from './ServersContext';
import { inviteLink } from './format';

export const EXPIRY_CHOICES = [
  { value: 1800, label: '30 minutes' },
  { value: 3600, label: '1 hour' },
  { value: 21600, label: '6 hours' },
  { value: 43200, label: '12 hours' },
  { value: 86400, label: '1 day' },
  { value: 604800, label: '7 days' },
  { value: null, label: 'Never' },
];
export const USES_CHOICES = [null, 1, 5, 10, 25, 50, 100];

const selectClass = 'w-full cursor-pointer rounded border border-solid border-border bg-surface px-3 py-2 font-body text-sm text-text outline-none focus:border-purple';

// Creates an invite link (7 days, unlimited by default) with options to change expiry and uses
export default function InviteDialog({ nodeId, onClose }) {
  const { conn } = useServers();
  const { st } = useServer(nodeId);
  const [expires, setExpires] = useState(604800);
  const [uses, setUses] = useState(null);
  const [invite, setInvite] = useState(null);
  const [error, setError] = useState('');
  const [busy, setBusy] = useState(false);
  const [copied, setCopied] = useState(false);
  const [editing, setEditing] = useState(false);

  const create = useCallback(async (expiresIn, maxUses) => {
    setBusy(true); setError('');
    try {
      const res = await conn(nodeId).post('/api/invites', { expires_in: expiresIn, max_uses: maxUses });
      setInvite(res);
      setEditing(false);
    } catch (err) {
      setError(err.message || 'Could not create an invite.');
    } finally { setBusy(false); }
  }, [conn, nodeId]);

  useEffect(() => { create(604800, null); }, [create]);

  const link = invite && /^[A-Za-z0-9]{6,16}$/.test(invite.code) ? inviteLink(nodeId, invite.code) : '';
  const copy = async () => {
    try { await navigator.clipboard.writeText(link); setCopied(true); setTimeout(() => setCopied(false), 1500); } catch {}
  };
  const expiryLabel = EXPIRY_CHOICES.find(c => c.value === expires)?.label;

  return (
    <Modal title={`Invite people to ${st.info?.name || 'the server'}`} onClose={onClose}>
      <div className="mb-2 text-sm text-text-muted">Send this link to people you want to invite.</div>
      <div className="flex items-center gap-2 rounded bg-surface px-3 py-2">
        <span className="min-w-0 flex-1 select-all truncate font-mono text-sm text-text">{link || (busy ? 'Creating…' : '')}</span>
        <button type="button" onClick={copy} disabled={!link} className="shrink-0 cursor-pointer rounded-pill border-0 bg-brand px-3.5 py-1.5 font-display text-xs font-bold text-white disabled:opacity-50">
          {copied ? 'Copied' : 'Copy'}
        </button>
      </div>
      {error && <div className="mt-2 text-xs text-red">{error}</div>}

      {!editing ? (
        <div className="mt-2 text-xs text-text-dim">
          {expires ? `Expires after ${expiryLabel}` : 'Never expires'}{uses ? `, ${uses} ${uses === 1 ? 'use' : 'uses'}` : ''}.{' '}
          <button type="button" onClick={() => setEditing(true)} className="cursor-pointer border-0 bg-transparent p-0 text-xs font-semibold text-purple-light hover:underline">Change</button>
        </div>
      ) : (
        <div className="mt-3 flex flex-col gap-2">
          <label className="text-xs font-bold text-text-dim">
            Expires after
            <select value={expires ?? ''} onChange={e => setExpires(e.target.value ? Number(e.target.value) : null)} className={`${selectClass} mt-1`}>
              {EXPIRY_CHOICES.map(c => <option key={c.label} value={c.value ?? ''}>{c.label}</option>)}
            </select>
          </label>
          <label className="text-xs font-bold text-text-dim">
            Max number of uses
            <select value={uses ?? ''} onChange={e => setUses(e.target.value ? Number(e.target.value) : null)} className={`${selectClass} mt-1`}>
              {USES_CHOICES.map(u => <option key={u ?? 'none'} value={u ?? ''}>{u ? `${u} ${u === 1 ? 'use' : 'uses'}` : 'No limit'}</option>)}
            </select>
          </label>
          <div className="mt-1 flex gap-2">
            <ModalButton onClick={() => setEditing(false)}>Cancel</ModalButton>
            <ModalButton primary disabled={busy} onClick={() => create(expires, uses)}>New link</ModalButton>
          </div>
        </div>
      )}
    </Modal>
  );
}
